import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchManufacturerAcceptances } from '../api';
import './ManufacturerAcceptances.css';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'With Pending' },
  { value: 'compliant', label: 'Fully Compliant' },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function statusLabel(status) {
  if (status.status === 'current') return 'Accepted';
  if (status.status === 'outdated') return 'Outdated';
  return 'Never Accepted';
}

function ManufacturerAcceptances({ accessToken, onForbidden }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const payload = await fetchManufacturerAcceptances(accessToken);
      setData(payload);
    } catch (err) {
      if (err.message === 'FORBIDDEN') {
        onForbidden?.();
        return;
      }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, onForbidden]);

  useEffect(() => {
    load();
  }, [load]);

  const manufacturers = data?.manufacturers || [];
  const docTypes = data?.doc_types || [];
  const titles = data?.titles || {};

  const filtered = useMemo(() => {
    if (filter === 'pending') return manufacturers.filter((m) => !m.is_fully_compliant);
    if (filter === 'compliant') return manufacturers.filter((m) => m.is_fully_compliant);
    return manufacturers;
  }, [manufacturers, filter]);

  const totalCount = manufacturers.length;
  const pendingCount = manufacturers.filter((m) => !m.is_fully_compliant).length;
  const compliantCount = totalCount - pendingCount;

  const handleExport = () => {
    if (filtered.length === 0) return;
    const headers = ['Manufacturer', 'Company', 'User ID'];
    for (const docType of docTypes) {
      headers.push(`${titles[docType] || docType} Status`);
      headers.push(`${titles[docType] || docType} Version`);
      headers.push(`${titles[docType] || docType} Accepted Date`);
    }
    const rows = filtered.map((m) => {
      const row = [
        `"${(m.full_name || '').replace(/"/g, '""')}"`,
        `"${(m.company_name || '').replace(/"/g, '""')}"`,
        m.user_id,
      ];
      for (const docType of docTypes) {
        const s = m.statuses[docType];
        row.push(statusLabel(s));
        row.push(s.version || '');
        row.push(s.accepted_at ? new Date(s.accepted_at).toISOString().split('T')[0] : '');
      }
      return row.join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manufacturer-acceptances-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="compliance-view">
      <div className="compliance-summary">
        <div className="compliance-summary-card">
          <span className="compliance-summary-label">Total Manufacturers</span>
          <span className="compliance-summary-value">{totalCount}</span>
        </div>
        <div className="compliance-summary-card">
          <span className="compliance-summary-label">With Pending</span>
          <span className="compliance-summary-value compliance-summary-pending">{pendingCount}</span>
        </div>
        <div className="compliance-summary-card">
          <span className="compliance-summary-label">Fully Compliant</span>
          <span className="compliance-summary-value compliance-summary-ok">{compliantCount}</span>
        </div>
      </div>

      {error && <div className="compliance-error">{error}</div>}

      <div className="compliance-table-wrapper">
        <div className="compliance-header-bar">
          <h2>Manufacturer Acceptances</h2>
          <div className="compliance-header-right">
            <button
              type="button"
              className="compliance-export-btn"
              onClick={handleExport}
              disabled={filtered.length === 0}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="compliance-refresh-btn"
              onClick={load}
              disabled={isLoading}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="compliance-tabs">
          {STATUS_FILTERS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`compliance-tab ${filter === tab.value ? 'compliance-tab-active' : ''}`}
              onClick={() => setFilter(tab.value)}
            >
              {tab.label}
              {tab.value === 'pending' && pendingCount > 0 && (
                <span className="compliance-tab-badge">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="compliance-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="compliance-empty">No manufacturers match this filter.</div>
        ) : (
          <div className="compliance-table">
            <div className="compliance-table-head">
              <span className="compliance-col compliance-col-manu">Manufacturer</span>
              {docTypes.map((docType) => (
                <span key={docType} className="compliance-col compliance-col-doc">
                  {titles[docType] || docType}
                </span>
              ))}
            </div>
            {filtered.map((m) => (
              <div
                key={m.user_id}
                className={`compliance-table-row ${m.is_fully_compliant ? '' : 'compliance-row-pending'}`}
              >
                <span className="compliance-col compliance-col-manu">
                  <span className="compliance-manu-name">{m.full_name || 'Unknown'}</span>
                  {m.company_name && (
                    <span className="compliance-manu-company">{m.company_name}</span>
                  )}
                </span>
                {docTypes.map((docType) => {
                  const s = m.statuses[docType];
                  const pillClass =
                    s.status === 'current'
                      ? 'compliance-pill-current'
                      : s.status === 'outdated'
                      ? 'compliance-pill-outdated'
                      : 'compliance-pill-never';
                  return (
                    <span
                      key={docType}
                      className="compliance-col compliance-col-doc"
                    >
                      <span className={`compliance-pill ${pillClass}`}>
                        {statusLabel(s)}
                      </span>
                      <span className="compliance-pill-detail">
                        {s.status === 'current' &&
                          `v${s.version} · ${formatDate(s.accepted_at)}`}
                        {s.status === 'outdated' &&
                          `v${s.version} accepted, current v${s.current_version}`}
                        {s.status === 'never_accepted' &&
                          (s.current_version ? `Current v${s.current_version}` : '—')}
                      </span>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManufacturerAcceptances;
